import type { CampoFormulario } from './OrcamentoFormShell';

export type PerfilId = 'empresa' | 'orgao' | 'arquiteto';

export interface PerfilConfig {
  id: PerfilId;
  titulo: string;
  resumo: string;
  formId: string;
  submitAddr: string;
  submitLabel: string;
  nota: string;
  campos: CampoFormulario[];
}

const CATEGORIAS = [
  'Cadeiras operacionais e ergonômicas',
  'Cadeiras executivas e giratórias',
  'Longarinas e bancos de espera',
  'Mesas administrativas e de reunião',
  'Estações de trabalho e plataformas',
  'Armários de aço, arquivos e gaveteiros',
  'Poltronas e cadeiras para auditório',
  'Mobiliário educacional e refeitório',
  'Divisórias, cabines e soluções acústicas',
  'Projeto completo de ambiente',
];

export const PERFIS: PerfilConfig[] = [
  {
    id: 'empresa',
    titulo: 'Empresa privada',
    resumo: 'Escritórios, clínicas, indústrias e empresas que precisam mobiliar ou repor ambientes.',
    formId: 'orcamento-empresa',
    submitAddr: 'https://readdy.ai/api/form/dbkg0ke2g81r77f5o790',
    submitLabel: 'Solicitar orçamento',
    nota: 'Usamos seus dados apenas para responder a esta solicitação comercial, conforme a LGPD.',
    campos: [
      { name: 'nome', label: 'Nome', type: 'text', required: true, placeholder: 'Seu nome completo' },
      { name: 'empresa', label: 'Empresa', type: 'text', required: true, placeholder: 'Razão social ou nome fantasia' },
      { name: 'cidade', label: 'Cidade', type: 'text', required: true, placeholder: 'Cidade / UF' },
      { name: 'whatsapp', label: 'WhatsApp', type: 'tel', required: true, placeholder: '(00) 00000-0000' },
      {
        name: 'solucao',
        label: 'Produto ou solução',
        type: 'select',
        required: true,
        options: CATEGORIAS,
      },
      { name: 'quantidade', label: 'Quantidade estimada', type: 'text', placeholder: 'Ex.: 12 cadeiras e 4 mesas' },
      {
        name: 'observacoes',
        label: 'Observações',
        type: 'textarea',
        placeholder: 'Conte o que o ambiente precisa, prazo desejado e se já existe especificação técnica.',
        maxLength: 500,
      },
    ],
  },
  {
    id: 'orgao',
    titulo: 'Órgão público',
    resumo: 'Prefeituras, secretarias, câmaras, universidades, escolas e unidades de saúde.',
    formId: 'cotacao-orgao-publico',
    submitAddr: 'https://readdy.ai/api/form/dbkg0ke2g81r77f5o79g',
    submitLabel: 'Solicitar cotação para minha instituição',
    nota: 'Não é necessário informar CNPJ ou número de processo neste primeiro contato. A solicitação não equivale a proposta formal em licitação.',
    campos: [
      { name: 'nome', label: 'Nome do responsável', type: 'text', required: true, placeholder: 'Seu nome completo' },
      {
        name: 'instituicao',
        label: 'Órgão ou instituição',
        type: 'text',
        required: true,
        placeholder: 'Ex.: Secretaria de Educação de ...',
      },
      { name: 'setor', label: 'Setor (opcional)', type: 'text', placeholder: 'Ex.: Compras, Patrimônio' },
      { name: 'municipio', label: 'Município', type: 'text', required: true, placeholder: 'Ex.: Caruaru' },
      { name: 'uf', label: 'UF', type: 'text', required: true, placeholder: 'Ex.: PE' },
      { name: 'email', label: 'E-mail profissional', type: 'email', required: true, placeholder: 'nome@orgao.gov.br' },
      { name: 'whatsapp', label: 'WhatsApp', type: 'tel', placeholder: '(00) 00000-0000' },
      { name: 'categoria', label: 'Categoria de produto', type: 'select', required: true, options: CATEGORIAS },
      { name: 'quantidade', label: 'Quantidade', type: 'text', placeholder: 'Ex.: 40 cadeiras operacionais' },
      {
        name: 'descricao',
        label: 'Descrição da demanda',
        type: 'textarea',
        required: true,
        placeholder: 'Descreva os itens, quantidades e especificações técnicas que já possui.',
        maxLength: 500,
      },
    ],
  },
  {
    id: 'arquiteto',
    titulo: 'Arquiteto ou especificador',
    resumo: 'Escritórios de arquitetura e engenharia que especificam mobiliário em projetos.',
    formId: 'atendimento-arquiteto',
    submitAddr: 'https://readdy.ai/api/form/dbkg0ke2g81r77f5o7a0',
    submitLabel: 'Falar sobre especificação',
    nota: 'Podemos apoiar com informações de fabricante, medidas e composição de ambientes conforme disponibilidade dos modelos.',
    campos: [
      { name: 'nome', label: 'Nome', type: 'text', required: true, placeholder: 'Seu nome completo' },
      { name: 'escritorio', label: 'Escritório', type: 'text', placeholder: 'Nome do escritório' },
      { name: 'municipio', label: 'Município', type: 'text', required: true, placeholder: 'Cidade / UF' },
      { name: 'whatsapp', label: 'WhatsApp', type: 'tel', required: true, placeholder: '(00) 00000-0000' },
      {
        name: 'tipo_projeto',
        label: 'Tipo de projeto',
        type: 'select',
        required: true,
        options: [
          'Escritório corporativo',
          'Clínica ou consultório',
          'Instituição de ensino',
          'Unidade de saúde',
          'Auditório ou plenário',
          'Restaurante ou food service',
          'Hotelaria',
          'Outro ambiente profissional',
        ],
      },
      { name: 'ambiente', label: 'Ambiente', type: 'select', options: CATEGORIAS },
      {
        name: 'etapa',
        label: 'Etapa de especificação',
        type: 'select',
        options: [
          'Estudo preliminar',
          'Anteprojeto',
          'Projeto executivo',
          'Planilha de quantitativos',
          'Negociação com o cliente',
        ],
      },
      {
        name: 'mensagem',
        label: 'Mensagem',
        type: 'textarea',
        required: true,
        placeholder: 'Descreva o ambiente, quantidades previstas e o que precisa do fornecedor.',
        maxLength: 500,
      },
    ],
  },
];